import React, { useState, useEffect, Fragment, } from "react";
import {
  Grid,
  Button,
  Link,
  Container,
  Alert,
  Skeleton,
  AlertTitle
} from "@mui/material";
import StringNumber from "../../common/fields/StringNumber";
import { verify2fa, getQrImageFor2faSetup } from "../../../services/LoginService";
import { useLocation, Navigate, useParams } from "react-router-dom";
import { getSafeRedirect } from "../../../utils/authRedirect";

const SecondAuth = () => {
  const [phase, setPhase] = useState("init"); // init | qr | input
  const [code, setCode] = useState("");
  const [method, setMethod] = useState(null);
  const [interimToken, setInterimToken] = useState(null);
  const [qrImage, setQrImage] = useState(null);
  const [showQrAuth, setShowQrAuth] = useState(false);
  const [credentialsError, setCredentialsError] = useState(false);
  const [responseLogin, setResponseLogin] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({ status: { code: false }, text: { code: "" } });
  const { code: codeFromURL, interimToken: tokenFromURL } = useParams();
  const location = useLocation();

  useEffect(() => {
    const state = location.state;

    if (state?.method && state?.token) {
      setMethod(state.method);
      setInterimToken(state.token);
      const wantsQr = state.showQrAuth || false;
      setShowQrAuth(wantsQr);
      setPhase(wantsQr ? "qr" : "input");
    } else if (tokenFromURL) {
      setMethod("email");
      setInterimToken(tokenFromURL);
      setCode(codeFromURL);
      setPhase("input");
    }
  }, [location.state, tokenFromURL]);

  useEffect(() => {
    if (phase !== "qr") return;

    const fetchQr = async () => {
      try {
        const res = await getQrImageFor2faSetup({ interimToken });
        if (res?.image && res?.interimToken) {
          setQrImage(res.image);
          setInterimToken(res.interimToken);
        } else {
          throw new Error("Invalid QR response");
        }
      } catch (err) {
        setCredentialsError(true);
      }
    };

    setCredentialsError(false);
    fetchQr();
  }, [phase]);


  const handleContinue = () => {
    setCredentialsError(false);
    setPhase("input");
  };

  const send = async () => {
    const trimmedCode = code.trim();
    const codeError = trimmedCode === "";

    setErrors({
      status: { code: codeError },
      text: { code: codeError ? "Enter a valid code" : "" }
    });

    if (codeError) return;

    setLoading(true);
    setCredentialsError(false);

    try {
      const res = await verify2fa({ code: trimmedCode, token: interimToken });

      if (res.userAuthenticated) {
        localStorage.setItem("user", res.location);
        localStorage.setItem("headersSetCookie", res.headersSetCookie);
        localStorage.setItem("additionalUserData", JSON.stringify(res.additionalUserData));
        sessionStorage.setItem("siderbarO", true);
        setResponseLogin(res);
      } else {
        throw new Error("Authentication failed");
      }
    } catch (err) {
      setCredentialsError(true);
      setResponseLogin(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (e) => {
    if (credentialsError) setCredentialsError(false);
    setCode(e.target.value);
  };

  const methodContent = {
    email: {
      title: "Code required",
      subtitle: "Please copy and paste the authentication code you received via e-mail."
    },
    totp: {
      title: "One time password required",
      subtitle: "Open your authenticator app and type in the one-time password."
    }
  };

  if (responseLogin && Object.keys(responseLogin).length > 0) {
    const safeRedirect = getSafeRedirect({
      pathname: location.state?.from?.pathname,
      search: location.state?.from?.search,
    })
    return <Navigate to={safeRedirect} replace />;
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    send();
  };

  return (
    <Fragment>
      <Container maxWidth="md" className="loginPageContainer">
        <div className="loginPageContainer">
          {phase === "qr" ? (
            <>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <div className="loginTitles">
                    <div className="title">Secure Access</div>
                    <div className="subtitle">
                      Your account is required to use 2 factor authentication. Scan the code in your 2FA application and once the configuration is completed click continue.
                    </div>
                  </div>
                </Grid>
              </Grid>

              {qrImage ? (
                <img src={qrImage} alt="2FA QR Code" style={{ maxWidth: "300px", margin: "20px 0" }} />
              ) : (
                <Skeleton variant="rectangular" height={300} width={300} style={{ margin: "20px 0" }} />
              )}

              <Button
                onClick={handleContinue}
                variant="contained"
                color="primary"
                size="large"
                disabled={!qrImage}
              >
                Continue
              </Button>
            </>
          ) : (
            <form className="form" noValidate onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <div className="loginTitles">
                    {method ? (
                      <>
                        <div className="title">{methodContent[method]?.title || "Authentication"}</div>
                        <div className="subtitle">{methodContent[method]?.subtitle || "Complete second step of authentication."}</div>
                      </>
                    ) : (
                      <>
                        <Skeleton variant="text" width={300} height={40} />
                        <Skeleton variant="text" width={400} height={25} />
                      </>
                    )}
                  </div>
                </Grid>
              </Grid>

              {credentialsError && (
                <Alert severity="error" style={{ marginBottom: '10px' }} >
                  <AlertTitle>Authentication failed</AlertTitle>
                  2FA verification failed or could not be processed.
                  <br />
                  Please try again or request a new code.
                </Alert>
              )}

              <Grid container spacing={6}>
                <Grid item xs={12}>
                  <div style={{ marginBottom: 16 }}>
                    <StringNumber
                      id="code"
                      label="Code"
                      name="code"
                      placeholder={method === "email" ? "Email code..." : "Authenticator code..."}
                      value={code}
                      disabled={loading}
                      error={errors.status.code}
                      errorText={errors.text.code}
                      classNameCustom={"textField"}
                      required
                      onChange={handleCodeChange}
                    />
                  </div>
                </Grid>
              </Grid>

              <hr />

              <div className="buttons-container">
                <Button component={Link} href={"/login"} variant="text" className="mr-1" size="medium">
                  Back to Login
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  className="submit"
                  size="large"
                  disabled={loading}
                >
                  {loading ? "Sending..." : "Sign-in"}
                </Button>
              </div>
            </form>
          )}
        </div>
      </Container>
    </Fragment>
  );
};

export default SecondAuth;
